import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import _, { keyBy } from 'lodash';
import { X } from 'lucide-react';
import { z } from 'zod';

import type { SitePreview } from '@/api/fe-api/domain/entities/site';
import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import { MultiSelect } from '@/components/form/inputs/multi-select';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Document } from '@/modules/documents/domain/entities/document';
import {
  DocumentType,
  DocumentTypeSchema,
} from '@/modules/documents/domain/entities/document-type';
import { UpdateDocument } from '@/modules/documents/view/mutations/update-document';
import { toastResult } from '@/utils/toast';

import { DocumentEditSiteWarning } from './document-edit-site-warning';

export type DocumentEditDialogProps = {
  document: Document;
  loading?: boolean;
  siteId?: string;
} & Omit<DialogProps, 'children'>;

export function DocumentEditDialog({
  document,
  siteId,
  open,
  onClose,
}: DocumentEditDialogProps) {
  const updateDocument = UpdateDocument.useMutation();
  const siteList = GetAllSites.useQuery();

  const siteIdMapper = useMemo<Record<string, SitePreview>>(
    () => keyBy(siteList.data ?? [], 'id'),
    [siteList.data],
  );

  const defaultValues: Partial<FormUpdateInputs> = useMemo(
    () => ({
      ...document,
      tags: document.tags.join(', '),
      siteIds: document.sites.map((site) => site.siteId),
    }),
    [document],
  );

  const form = useForm<FormUpdateInputs>({
    defaultValues,
    resolver: zodResolver(formUpdateSchema),
  });

  const { isDirty, isValid } = form.formState;

  async function handleOnUpdate(data: FormUpdateInputs) {
    const update = updateDocument.mutateAsync({
      siteIds: siteId ? [siteId] : [],
      document: {
        ...data,
        tags: data.tags
          ?.split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      },
    });
    const result = await toastResult(update, {
      success: `Document "${data?.name ?? document.name}" updated successfully`,
      loading: 'Saving changes...',
      error: `Failed to update "${data?.name ?? document.name}"`,
      exception: 'Failed to update document',
    });

    if (result.isOk()) {
      onClose(true);
    }
  }

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <Dialog size="sm" className="my-auto w-96" onClose={onClose} open={open}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleOnUpdate)}>
          <DialogTitle>Edit Document</DialogTitle>
          <DialogDescription>
            Modify document details.
            {document.sites.length > 1 ? (
              <>
                {' '}
                Changes to this document affect the following sites:
                <ul className="mt-1 list-disc pl-6">
                  {document.sites.map((s) => (
                    <li key={s.siteId}>{s.name}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </DialogDescription>
          <DialogBody className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>File name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="siteIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Associated sites</FormLabel>
                  <FormControl>
                    <>
                      <MultiSelect<string>
                        className="h-10 ring-neutral-200"
                        placeholder="No sites"
                        value={field.value}
                        onChange={field.onChange}
                        rightIcon={
                          field.value.length > 0 ? (
                            <X
                              className="pointer-events-auto cursor-pointer text-gray-400"
                              onClick={() => field.onChange([])}
                            />
                          ) : undefined
                        }
                        options={Object.values(siteIdMapper)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((s) => s.id)}
                        renderValue={(v) =>
                          v
                            .map((siteId) => siteIdMapper[siteId]?.name ?? '')
                            .join(', ')
                        }
                        renderOption={(v) => siteIdMapper[v]?.name ?? ''}
                        loading={siteList.isFetching}
                      />
                      <DocumentEditSiteWarning
                        siteId={siteId}
                        newSiteIds={field.value}
                      />
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        {Object.values(DocumentType).map((type) => (
                          <SelectItem key={type} value={type.toString()}>
                            {type.toString()}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags (optional)</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription>
                    Tags are separated by a comma (,)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </DialogBody>
          <DialogActions>
            <Button type="submit" disabled={!isDirty || !isValid}>
              Save changes
            </Button>
          </DialogActions>
        </form>
      </Form>
    </Dialog>
  );
}

const formUpdateSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  siteIds: z.array(z.string()),
  type: DocumentTypeSchema,
  tags: z.string().optional(),
});
type FormUpdateInputs = z.infer<typeof formUpdateSchema>;
